import { formatDate } from "date-fns";
import { Mutex } from "es-toolkit";
import type { Options } from "ky";
import ky from "ky";

export const formatDateTime = (
  datetime: string,
  format: string = "yyyy.MM.dd",
) => formatDate(new Date(datetime), format);

export const replaceLineBreaks = (s: string) =>
  s.replace(/(\r\n|\r|\n)/g, "<br/>");

export const splitLineBreaks = (s: string) =>
  s.split(/\r\n|\r|\n/).filter(Boolean);

export const createMarkup = (content?: string) => ({
  __html: replaceLineBreaks(content ?? ""),
});

export const setComma = (num: number, maximumFractionDigits?: number): string =>
  num.toLocaleString(undefined, {
    maximumFractionDigits: maximumFractionDigits ?? 2,
  });

export const toNTCurrency = (
  num: number,
  maximumFractionDigits?: number,
): string => `NT$${setComma(num, maximumFractionDigits)}`;

export const stripHtml = (value: string) =>
  value
    .replace(/<[^>]*>?/gm, " ") // 태그를 공백으로 치환하여 단어 결합 방지
    .replace(/\s+/g, " ") // 다중 공백 및 줄바꿈을 단일 공백으로 축소
    .trim();

export const TAIWAN_DIAL_CODE = "+886";

/**
 * 사용자가 입력한 대만 로컬 번호를 +886 국제 표기로 정규화한다.
 * 비숫자 문자를 제거하고 선행 886/0을 떼어낸 뒤 +886을 결합한다.
 * 예) "0912345678" -> "+886912345678"
 */
export const toTaiwanPhoneNumber = (input: string): string => {
  const digits = input
    .replace(/\D/g, "")
    .replace(/^886/, "")
    .replace(/^0+/, "");
  return `${TAIWAN_DIAL_CODE}${digits}`;
};

const throttleFetchMutex = new Mutex();

export async function throttledFetch(url: string, options?: Options) {
  if (throttleFetchMutex.isLocked) {
    return null;
  }

  await throttleFetchMutex.acquire();
  try {
    const response = await ky.get(url, options).json();
    return response;
  } finally {
    throttleFetchMutex.release();
  }
}

export const isValidExternalUrl = (url: string) => {
  return url.startsWith("http://") || url.startsWith("https://");
};

export const isInternalUrl = (url: string) => {
  return url.startsWith("https://seoulmoment.com.tw/");
};

export const isValidId = (id: number) => {
  return Number.isInteger(id) && id > 0;
};
export interface JWTPayload {
  id: number;
  jwtType: string;
  iat: number;
  exp: number;
}

export const decodeJWT = <T = JWTPayload>(token: string): T | null => {
  try {
    const payload = token.split(".")[1];
    if (!payload) return null;

    const base64 = payload.replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64.padEnd(
      base64.length + ((4 - (base64.length % 4)) % 4),
      "=",
    );
    const bytes = Uint8Array.from(atob(padded), (c) => c.charCodeAt(0));
    return JSON.parse(new TextDecoder().decode(bytes)) as T;
  } catch {
    return null;
  }
};
